import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import styles from "./ChatArea.module.scss";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from "@mui/material";
import { useAxios } from "myAxios";
import { useAppActions } from "../../store/AppStore";
import ChatAreaInputTagsGroup from "./ChatAreaInputTagsGroup";
import ChatAreaInputAccountBtn from "./ChatAreaInputAccountBtn";
import { useModel } from "hooks/useModel";
import AccountDialog from "../AccountDialog";
import QuickFavoritePrompts from "./QuickFavoritePrompts";
import ChatAreaInputModelBtn from "./ChatAreaInputModelBtn";
import { infoTagsToMentions, isInfoTagLoading } from "components/ai-chat/utils/tags";
import { FEATURE_TOGGLES } from "constant";
import { AuthProvider } from "pages";

export interface ChatAreaInputRef {
  sendMessage: (
    input: string,
    prompt?: { description: string; title: string; prompt: string }
  ) => void;
}
const ChatAreaInput = forwardRef<ChatAreaInputRef>((props, ref) => {
  const {
    currentAccountTags,
    setCurrentAccountTags,
    drawerWidth,
    fullScreen,
    accountDialogConfig,
    setAccountDialogConfig,
    selectedKey,
    setChatlist,
    setUnsendMessages,
    isMcpService,
    setIsMcpService,
  } = useModel("aichat");
  const [input, setInput] = useState("");
  const [showAccount, setShowAccount] = useState(true);
  const [showQuickPrompts, setShowQuickPrompts] = useState(false);
  const quickPromptsRef = useRef<HTMLDivElement | null>(null);
  const axios = useAxios();
  const { sendMessageFn } = useAppActions(axios);
  const hasLoadingAccounts = currentAccountTags.some(isInfoTagLoading);
  const authDetail = useContext(AuthProvider);
  const aiChatMcpEnabled = !!authDetail?.toggles?.[FEATURE_TOGGLES.AI_CHAT_MCP];

  const syncTopicMentions = useCallback(
    (tags: typeof currentAccountTags) => {
      if (!selectedKey) return;

      setChatlist((prev) =>
        prev.map((item) =>
          item.id === selectedKey
            ? {
                ...item,
                accountTags: tags,
                mentions: infoTagsToMentions(tags),
              }
            : item
        )
      );
    },
    [selectedKey, setChatlist]
  );

  const handleServiceModeChange = (event: SelectChangeEvent<string>) => {
    const nextIsMcpService = event.target.value === "agent";
    setIsMcpService(nextIsMcpService);
    if (nextIsMcpService) {
      setUnsendMessages([]);
    }
    if (!nextIsMcpService) {
      setCurrentAccountTags((prev) => {
        const nextTags = prev.filter((tag) => typeof tag.id === "string");
        syncTopicMentions(nextTags);
        return nextTags;
      });
    }
  };

  useImperativeHandle(ref, () => ({
    sendMessage: (i, prompt) => {
      sendMessageFn(i, setInput, currentAccountTags, prompt);
    },
  }));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showQuickPrompts &&
        quickPromptsRef.current &&
        !quickPromptsRef.current.contains(e.target as Node)
      ) {
        setShowQuickPrompts(false);
      }
    };
    if (showQuickPrompts) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showQuickPrompts]);

  useEffect(() => {
    if (!aiChatMcpEnabled && isMcpService) {
      setIsMcpService(false);
      setCurrentAccountTags((prev) => {
        const nextTags = prev.filter((tag) => typeof tag.id === "string");
        syncTopicMentions(nextTags);
        return nextTags;
      });
    }
  }, [aiChatMcpEnabled, isMcpService, setCurrentAccountTags, setIsMcpService, syncTopicMentions]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessageFn(input, setInput, currentAccountTags);
    }
  };

  return (
    <div
      className={styles.chatAreaInput}
      style={{
        width: fullScreen ? "100%" : drawerWidth,
      }}
    >
      <div className={styles.inputContent}>
        <ChatAreaInputTagsGroup showAccount={showAccount} />
        <TextField
          id="message-input"
          variant="outlined"
          placeholder="Ask anything or type '/' to access Favorite Prompts..."
          multiline
          minRows={1}
          maxRows={2}
          value={input}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
            },
          }}
          onChange={(e) => {
            const value = e.target.value;
            setInput(value);
            if (value === "/") {
              setShowQuickPrompts(true);
            } else if (showQuickPrompts) {
              setShowQuickPrompts(false);
            }
          }}
          onKeyDown={onKeyDown}
          className={styles.textField}
        />
        <Box className={styles.inputFooter}>
          <div className={styles.footerButton}>
            <Tooltip
              title={`${showAccount ? "Hide" : "Show"} Customer Info (${currentAccountTags.length})`}
            >
              <IconButton
                className={styles.accountButton}
                onClick={() => setShowAccount(!showAccount)}
              >
                <img
                  src={`/aichat/${showAccount ? "customerInfoActive" : "customerInfo"}.svg`}
                  alt=""
                />
                <span
                  className={styles.accountCount}
                  style={{
                    backgroundColor: showAccount ? "#3B82F6" : "#9CA3AF",
                  }}
                >
                  {currentAccountTags.length}
                </span>
              </IconButton>
            </Tooltip>
            {aiChatMcpEnabled && (
              <Select
                className={styles.serviceModeSelect}
                size="small"
                value={isMcpService ? "agent" : "chat"}
                onChange={handleServiceModeChange}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="agent">Agent</MenuItem>
              </Select>
            )}
            <ChatAreaInputAccountBtn
              disabled={hasLoadingAccounts}
              onClick={() => setAccountDialogConfig({ open: true })}
            />
            <ChatAreaInputModelBtn />
          </div>
          <div className={styles.footerButton}>
            <Tooltip title="Quick Favorite Prompts">
              <IconButton
                size="small"
                style={{ padding: 0 }}
                onClick={() => setShowQuickPrompts(!showQuickPrompts)}
              >
                <img
                  src="/aichat/quickPrompts.svg"
                  alt=""
                />
              </IconButton>
            </Tooltip>
            <div
              className={styles.sendButton}
              style={{
                backgroundImage:
                  !currentAccountTags.some(isInfoTagLoading) && input.trim() !== ""
                    ? "url(/aichat/sendblue.svg)"
                    : "url(/aichat/sendblack.svg)",
              }}
              onClick={() => {
                if (!hasLoadingAccounts && input.trim() !== "") {
                  sendMessageFn(input, setInput, currentAccountTags);
                }
              }}
            ></div>
          </div>
          {showQuickPrompts && (
            <QuickFavoritePrompts
              ref={quickPromptsRef}
              open={showQuickPrompts}
              onClose={() => setShowQuickPrompts(false)}
              setLocalInput={setInput}
            />
          )}
          <AccountDialog
            open={accountDialogConfig.open}
            promptData={accountDialogConfig?.promptData}
            close={() => setAccountDialogConfig({ open: false })}
            type="multiple"
            defaultTag={currentAccountTags}
            onOk={(e) => {
              setCurrentAccountTags(e);
              syncTopicMentions(e);
            }}
          />
        </Box>
      </div>
    </div>
  );
});

ChatAreaInput.displayName = "ChatAreaInput";

export default ChatAreaInput;
